import { IOrder, IRoute, IWaypoint } from '@src/pages/DeliveryDashboard/models/model-interface';
import PIECard, { PIECardModes } from '@src/shared/components/PIECard';
import { IconLocationPinFilled, IconUser } from '@justeattakeaway/pie-icons-react';
import PIEText from '@src/shared/components/PIEText';
import React, { FC } from 'react';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { globalStyles } from 'snacks-design-system';
import styles from './CourierDetailDeliveredOrderCard.module.scss';
import TagDelivered from '../../../DashboardCustomTag/components/TagDelivered';
import dayjs from 'dayjs';
import { DETAILED_ZOOM_LEVEL } from '@src/pages/DeliveryDashboard/models/model-constants';
import { DeliveryDashboardScreens } from '@src/pages/DeliveryDashboard/models/model-enum';
import { useDashboardUIStore } from '@src/pages/DeliveryDashboard/store/dashboardUIStore';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { useDashboardMapStore } from '@src/pages/DeliveryDashboard/store/dashboardMapStore';
import useDashboardStore from '@src/pages/DeliveryDashboard/hooks/use-dashboard-store';

interface ICourierDetailDeliveredOrderCardProps {
  orderData: IOrder;
}

const CourierDetailDeliveredOrderCard: FC<ICourierDetailDeliveredOrderCardProps> = ({
  orderData,
}) => {
  const { xdsSpacingD } = globalStyles;
  const { setActiveScreen, setActiveOrderHash, setActiveCourierId, activeCourierId } =
    useDashboardUIStore();
  const { setMapPanOffset, setMapZoomLevel, setMapCenter } = useDashboardMapStore();
  const { isResolutionMobile } = useDeviceDetect();
  const { courierRoute } = useDashboardStore();

  const getOrderWaypointDeliveredIndex = (): number | undefined => {
    const waypoints =
      (courierRoute &&
        courierRoute.find((r: IRoute) => r.courier.id === orderData.courier?.id)?.waypoints) ||
      [];
    if (!waypoints) {
      return undefined;
    }

    const sorted = waypoints
      .filter((waypoint: IWaypoint) => waypoint.isDelivered === true)
      .sort(
        (a: IWaypoint, b: IWaypoint) =>
          dayjs(a.lastUpdatedAt).valueOf() - dayjs(b.lastUpdatedAt).valueOf()
      );
    const waypointIndex = sorted.findIndex(
      (waypoint: IWaypoint) =>
        waypoint.order.uuid === orderData.uuid && waypoint.isDelivered === true
    );
    return waypointIndex !== -1 ? waypointIndex + 1 : 0;
  };
  getOrderWaypointDeliveredIndex();
  return (
    <PIEComponentUtil marginBottom={xdsSpacingD} marginTop={xdsSpacingD}>
      <PIECard
        className={styles.courierDetailDeliveredOrderCard}
        mode={PIECardModes.REGULAR}
        modElevation={true}
        onClick={() => {
          setActiveOrderHash(orderData.hash);
          activeCourierId ? setActiveCourierId(null) : null;
          setActiveScreen(DeliveryDashboardScreens.ORDER_DETAIL_SCREEN);
          setMapCenter({
            lat: orderData.location.latitude,
            lng: orderData.location.longitude,
          });
          setMapZoomLevel(DETAILED_ZOOM_LEVEL);
          isResolutionMobile ? setMapPanOffset(70) : null;
        }}
      >
        <div className={styles.courierDetailDeliveredOrderCard__items}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <PIEText
              truncate={true}
              size="var(--xds-font-size-14)"
              fontWeight="var(--xds-font-weight-semibold)"
              text={
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: '8px' }}
                >
                  <IconLocationPinFilled size="xs" /> {getOrderWaypointDeliveredIndex()}
                </div>
              }
            ></PIEText>
            <IconUser size="xs" />
            <PIEText
              truncate={true}
              size="var(--xds-font-size-16)"
              text={orderData.customer.lastName || ''}
            ></PIEText>
          </div>
          <div className={styles.courierDetailDeliveredOrderCard__tags}>
            <TagDelivered deliveredTime={dayjs(orderData.updateDate).format('HH:mm')} />
          </div>
        </div>
      </PIECard>
    </PIEComponentUtil>
  );
};

export default CourierDetailDeliveredOrderCard;
